import { useEffect } from 'react';
import { useUIConfig, type UIConfig } from '@/hooks/useUIConfig';

/**
 * Global CSS variable injector — reads UIConfig and sets CSS custom properties
 * on <html> so ALL pages/components automatically pick up admin-configured styles.
 */
const UIConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const config = useUIConfig();

  useEffect(() => {
    const root = document.documentElement;

    // Button styles
    root.style.setProperty('--ui-btn-primary-bg', config.btn_primary_bg);
    root.style.setProperty('--ui-btn-primary-text', config.btn_primary_text);
    root.style.setProperty('--ui-btn-secondary-bg', config.btn_secondary_bg);
    root.style.setProperty('--ui-btn-secondary-text', config.btn_secondary_text);
    root.style.setProperty('--ui-btn-radius', `${config.btn_border_radius}px`);

    // Card styles
    root.style.setProperty('--ui-card-radius', `${config.card_border_radius}px`);
    root.style.setProperty('--ui-card-shadow', config.card_shadow);
    root.style.setProperty('--ui-card-border-color', config.card_border_color || 'hsl(var(--border))');
    root.style.setProperty('--ui-card-bg', config.card_bg);

    // Badge styles
    root.style.setProperty('--ui-badge-verified-bg', config.badge_verified_bg);
    root.style.setProperty('--ui-badge-verified-text', config.badge_verified_text);
    root.style.setProperty('--ui-badge-open-bg', config.badge_open_bg);
    root.style.setProperty('--ui-badge-open-text', config.badge_open_text);
    root.style.setProperty('--ui-badge-closed-bg', config.badge_closed_bg);
    root.style.setProperty('--ui-badge-closed-text', config.badge_closed_text);
    root.style.setProperty('--ui-badge-radius', `${config.badge_border_radius}px`);

    // Image styles
    root.style.setProperty('--ui-img-radius', `${config.img_border_radius}px`);
    root.style.setProperty('--ui-img-aspect', config.img_aspect_ratio);
    root.style.setProperty('--ui-img-fit', config.img_object_fit);
    root.style.setProperty('--ui-img-border-color', config.img_border_color || 'transparent');
    root.style.setProperty('--ui-img-border-width', `${config.img_border_width}px`);
    root.style.setProperty('--ui-img-placeholder-bg', config.img_placeholder_bg);

    // Gradient styles
    root.style.setProperty('--ui-gradient-hero', config.gradient_hero || 'linear-gradient(135deg, hsl(213 69% 35%) 0%, hsl(213 60% 45%) 40%, hsl(152 62% 40%) 100%)');
    root.style.setProperty('--ui-gradient-primary', config.gradient_primary || 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))');
    root.style.setProperty('--ui-gradient-cta', config.gradient_cta || 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))');
    root.style.setProperty('--ui-gradient-card', config.gradient_card || 'linear-gradient(180deg, #ffffff, hsl(207 47% 97%))');

    // Section backgrounds
    root.style.setProperty('--ui-section-bg', config.section_bg || 'transparent');
    root.style.setProperty('--ui-auth-bg', config.auth_bg || 'linear-gradient(135deg, hsl(207 47% 97%), white, hsl(213 69% 39% / 0.05))');
    root.style.setProperty('--ui-footer-bg', config.footer_bg || 'hsl(215 30% 12%)');
    root.style.setProperty('--ui-footer-text', config.footer_text || '#ffffff');

    // Category card styles
    root.style.setProperty('--ui-category-radius', `${config.category_card_radius ?? config.card_border_radius}px`);
    root.style.setProperty('--ui-category-icon-radius', `${config.category_icon_radius ?? 16}px`);

    // Dashboard accent
    root.style.setProperty('--ui-dashboard-accent', config.dashboard_accent || 'hsl(213 69% 39%)');

  }, [config]);

  return <>{children}</>;
};

export default UIConfigProvider;
